"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

interface NavItem {
  label: string;
  href?: string;
  badge?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: "Get started",
    items: [
      { label: "Introduction", href: "/" },
      { label: "Quickstart", href: "/quickstart" },
      { label: "Authentication", href: "/authentication" },
      { label: "Versioning", href: "/versioning" },
      { label: "Rate limits", href: "/rate-limits" },
      { label: "Errors", href: "/errors" },
      { label: "Pagination", href: "/pagination" },
    ],
  },
  {
    label: "OAuth apps",
    items: [
      { label: "Overview", href: "/oauth" },
      { label: "Creating an app", href: "/oauth/creating-an-app" },
      {
        label: "Authorization code flow",
        href: "/oauth/authorization-code-flow",
      },
      { label: "Scopes", href: "/oauth/scopes" },
      { label: "Refreshing tokens", href: "/oauth/refreshing-tokens" },
      { label: "Revoking access", href: "/oauth/revoking-access" },
    ],
  },
  {
    label: "REST — Basic",
    items: [
      { label: "GET /me", href: "/api/rest/me" },
      { label: "GET /teams", href: "/api/rest/teams" },
    ],
  },
  {
    label: "REST — Materials",
    items: [
      { label: "GET /materials", href: "/api/rest/materials" },
      { label: "GET /materials/:id", href: "/api/rest/materials-retrieve" },
      {
        label: "GET /materials/:id/properties/:propertyId",
        href: "/api/rest/materials-property",
      },
      {
        label: "POST /materials/:id/properties/pull",
        href: "/api/rest/materials-properties-pull",
      },
    ],
  },
  {
    label: "REST — Processes",
    items: [
      { label: "GET /processes", href: "/api/rest/processes" },
      { label: "GET /processes/:id", href: "/api/rest/processes-retrieve" },
      {
        label: "GET /processes/:id/impacts/:impactId",
        href: "/api/rest/processes-impact",
      },
      {
        label: "POST /processes/:id/pull",
        href: "/api/rest/processes-pull",
      },
      {
        label: "POST /processes/:id/impacts/pull",
        href: "/api/rest/processes-impacts-pull",
      },
    ],
  },
  {
    label: "REST — Impact methods",
    items: [
      { label: "GET /impact-methods", href: "/api/rest/impact-methods" },
    ],
  },
  {
    label: "API reference — GraphQL",
    items: [
      { label: "Schema overview", href: "/api/graphql" },
      { label: "Queries", href: "/api/graphql/queries" },
      { label: "Mutations", href: "/api/graphql/mutations" },
    ],
  },
  {
    label: "Webhooks",
    items: [
      { label: "Overview", href: "/webhooks" },
      { label: "Event catalog", href: "/webhooks/events" },
      { label: "Verifying signatures", href: "/webhooks/verifying-signatures" },
    ],
  },
  {
    label: "More",
    items: [
      { label: "SDKs", href: "/sdks", badge: "Soon" },
      { label: "Changelog", href: "/changelog" },
    ],
  },
];

export function Sidebar() {
  const pathname = usePathname();

  return (
    <nav className="text-sm">
      <Link
        href="/"
        className="flex items-center gap-2 px-4 py-6 border-b border-border"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-accent" />
        <span className="font-semibold text-text-primary tracking-tight">
          Circa Docs
        </span>
      </Link>
      <div className="py-4">
        {SECTIONS.map((section) => (
          <div key={section.label} className="px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-text-tertiary font-medium mb-2">
              {section.label}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = item.href === pathname;
                return (
                  <li key={item.label}>
                    <Link
                      href={item.href ?? "#"}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md transition-colors ${
                        active
                          ? "bg-accent-soft text-accent border-l-2 border-accent pl-1.5"
                          : "text-text-secondary hover:text-text-primary hover:bg-surface"
                      }`}
                    >
                      <span className="truncate">{item.label}</span>
                      {item.badge ? (
                        <span className="text-[10px] uppercase tracking-wider bg-surface-elevated text-text-tertiary px-1.5 py-0.5 rounded">
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}
